import { useW2SendStatusGetApi } from '@/common/api/modules/w2Integration'
import { useSiteId } from '@/common/app/site'

import Heading from '@/components/view/atoms/heading/Heading'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import DetailPageLoader from '@/components/container/DetailPageLoader'

const Metric = ({ label, value }: { label: string; value: number }) => (
  <div className='border-border-input flex-1 rounded-lg border p-3 text-center'>
    <div className='text-size-24 text-primary font-bold'>{value}</div>
    <Text size={12} color={TextColor.SECONDARY}>
      {label}
    </Text>
  </div>
)

const W2SendStatusPage = () => {
  const siteId = useSiteId()
  const { data, error, isLoading } = useW2SendStatusGetApi({ site_id: siteId })

  return (
    <Main type={MainLayoutType.MEDIUM}>
      <PageTitle
        title='W2Commerce送信データ状況'
        description='W2Commerce へ送信するデータ（ターゲットリスト）の件数と送信状況を一覧します。'
      />
      <Content>
        <DetailPageLoader error={error} data={!isLoading && data}>
          {data && (
            <>
              <Area>
                <Heading>送信状況サマリ</Heading>
                <AreaContent>
                  <div className='flex flex-wrap gap-3'>
                    <Metric label='対象件数' value={data.total_items} />
                    <Metric label='未送信' value={data.total_unsent} />
                    <Metric label='送信済み' value={data.total_sent} />
                    <Metric label='エラー' value={data.total_error} />
                  </div>
                  {!data.enabled && (
                    <div className='mt-3'>
                      <Text size={12} color={TextColor.SECONDARY}>
                        ※ W2Commerce送信設定が無効です（サイト管理のAPI連携で有効化してください）。
                      </Text>
                    </div>
                  )}
                </AreaContent>
              </Area>

              <Area>
                <Heading>ターゲットリスト別の状況</Heading>
                <AreaContent>
                  <Table>
                    <thead>
                      <TableRow head>
                        <TableHeadItem>リスト名</TableHeadItem>
                        <TableHeadItem>W2ターゲットリストID</TableHeadItem>
                        <TableHeadItem>対象</TableHeadItem>
                        <TableHeadItem>未送信</TableHeadItem>
                        <TableHeadItem>送信済み</TableHeadItem>
                        <TableHeadItem>エラー</TableHeadItem>
                        <TableHeadItem>最終送信</TableHeadItem>
                      </TableRow>
                    </thead>
                    <tbody>
                      {data.rows.map((r) => (
                        <TableRow key={r.target_list_id}>
                          <TableRowItem>{r.name}</TableRowItem>
                          <TableRowItem>{r.w2_target_list_id}</TableRowItem>
                          <TableRowItem>{r.total}</TableRowItem>
                          <TableRowItem>{r.unsent}</TableRowItem>
                          <TableRowItem>{r.sent}</TableRowItem>
                          <TableRowItem>
                            <Text color={r.error > 0 ? TextColor.CRITICAL : TextColor.PRIMARY}>
                              {r.error}
                            </Text>
                          </TableRowItem>
                          <TableRowItem>
                            {r.last_sent_at ? r.last_sent_at.slice(0, 16).replace('T', ' ') : '-'}
                          </TableRowItem>
                        </TableRow>
                      ))}
                      {data.rows.length === 0 && (
                        <TableRow>
                          <TableRowItem>
                            <Text color={TextColor.SECONDARY}>送信データはありません。</Text>
                          </TableRowItem>
                        </TableRow>
                      )}
                    </tbody>
                  </Table>
                </AreaContent>
              </Area>
            </>
          )}
        </DetailPageLoader>
      </Content>
    </Main>
  )
}

export default W2SendStatusPage
